const MEASUREMENT_ID_PATTERN = /^G-[A-Z0-9]+$/i;
const SENSITIVE_QUERY_PARAMETER = /^(access_token|auth|code|email|key|password|secret|session|token)$/i;
const SCROLL_THRESHOLDS = [25, 50, 75, 90];

let initialized = false;

function measurementId() {
    return process.env.REACT_APP_GA_MEASUREMENT_ID?.trim();
}

function analyticsEnabled() {
    const id = measurementId();
    const isProduction = process.env.NODE_ENV === 'production';
    const debugEnabled = process.env.REACT_APP_ANALYTICS_DEBUG === 'true';

    return MEASUREMENT_ID_PATTERN.test(id || '') && (isProduction || debugEnabled);
}

function cleanUrl(value) {
    if (!value) return undefined;

    try {
        const url = new URL(value, window.location.origin);
        [...url.searchParams.keys()].forEach((key) => {
            if (SENSITIVE_QUERY_PARAMETER.test(key)) {
                url.searchParams.delete(key);
            }
        });
        url.hash = '';
        return url.toString();
    } catch {
        return undefined;
    }
}

function sendEvent(name, parameters = {}) {
    if (!analyticsEnabled() || typeof window.gtag !== 'function') return;
    window.gtag('event', name, parameters);
}

export function trackPageView() {
    const pageLocation = cleanUrl(window.location.href);
    const sanitizedPage = pageLocation ? new URL(pageLocation) : null;

    sendEvent('page_view', {
        page_title: document.title,
        page_location: pageLocation,
        page_path: sanitizedPage ? `${sanitizedPage.pathname}${sanitizedPage.search}` : window.location.pathname,
        page_referrer: cleanUrl(document.referrer),
    });
}

export function identifyUser(userId) {
    if (!analyticsEnabled() || typeof window.gtag !== 'function' || !userId) return;

    // Only pass an internal account ID here. Google Analytics forbids names,
    // email addresses, and other directly identifying information.
    window.gtag('config', measurementId(), { user_id: String(userId) });
}

function linkDetails(anchor) {
    const rawHref = anchor.getAttribute('href');
    if (!rawHref) return null;

    const text = anchor.textContent?.replace(/\s+/g, ' ').trim().slice(0, 100) || undefined;

    if (rawHref.startsWith('mailto:')) {
        return { event: 'contact_click', parameters: { contact_method: 'email', link_text: text } };
    }

    let url;
    try {
        url = new URL(rawHref, window.location.href);
    } catch {
        return null;
    }

    const parameters = {
        link_domain: url.hostname,
        link_text: text,
        link_url: cleanUrl(url.toString()),
    };

    if (url.pathname.toLowerCase().endsWith('.pdf')) {
        return {
            event: 'file_download',
            parameters: { ...parameters, file_name: url.pathname.split('/').pop() },
        };
    }

    if (url.origin !== window.location.origin) {
        return { event: 'outbound_link_click', parameters };
    }

    if (url.pathname !== window.location.pathname) {
        return { event: 'internal_navigation', parameters };
    }

    return null;
}

function installInteractionTracking() {
    document.addEventListener('click', (event) => {
        const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
        if (!anchor) return;

        const details = linkDetails(anchor);
        if (details) sendEvent(details.event, details.parameters);
    });

    const recordedThresholds = new Set();
    window.addEventListener('scroll', () => {
        const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollableHeight <= 0) return;

        const depth = Math.round((window.scrollY / scrollableHeight) * 100);
        SCROLL_THRESHOLDS.forEach((threshold) => {
            if (depth >= threshold && !recordedThresholds.has(threshold)) {
                recordedThresholds.add(threshold);
                sendEvent('scroll_depth', { percent_scrolled: threshold });
            }
        });
    }, { passive: true });
}

export function initializeAnalytics() {
    if (initialized || !analyticsEnabled()) return false;
    initialized = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        window.dataLayer.push(arguments);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId())}`;
    script.dataset.googleAnalytics = measurementId();
    document.head.appendChild(script);

    window.gtag('js', new Date());
    window.gtag('config', measurementId(), {
        anonymize_ip: true,
        send_page_view: false,
        transport_type: 'beacon',
    });

    trackPageView();
    installInteractionTracking();
    return true;
}
